<script setup lang="ts">
import documentLight from '@/assets/icons/document-light.svg';
// import documentDark from '@/assets/icons/document-dark.svg';
import clockLight from '@/assets/icons/clock-light.svg';
// import clockDark from '@/assets/icons/clock-dark.svg';
import mobilePhoneLight from '@/assets/icons/mobile-phone-light.svg';
// import mobilePhoneDark from '@/assets/icons/mobile-phone-dark.svg';
import houseLight from '@/assets/icons/house-light.svg';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import StartItem from '../ui/StartItem.vue';
import { ref, onUnmounted, computed, onBeforeMount } from 'vue';
import ExternalLink from '../ui/ExternalLink.vue';
import { useKycSessionStore } from '@/stores/kycSession.store';
import { storeToRefs } from 'pinia';
import type { Document } from '@/integrations/Immersve/types';
import { Regions } from '@/models/Regions';
import Mixpanel from '@/integrations/Mixpanel';
// import immersveLogoLight from '@/assets/icons/immersve-logo-light.svg';
// import x_light from '@/assets/icons/x-light.svg';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

//dark mode tracking
const isDarkTheme = ref(
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
);

function updateTheme(e: MediaQueryListEvent) {
  isDarkTheme.value = e.matches;
}

if (window.matchMedia) {
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', updateTheme);
  onUnmounted(() => {
    mediaQuery.removeEventListener('change', updateTheme);
  });
}

const icons = computed(() => {
  // TODO: this is hardcoded to light mode, need to fix if we want to add darkmode
  return {
    document: documentLight,
    clock: clockLight,
    mobilePhone: mobilePhoneLight,
    house: houseLight,
  };
  // return !isDarkTheme.value
  //   ? {
  //       document: documentDark,
  //       clock: clockDark,
  //       mobilePhone: mobilePhoneDark,
  //     }
  //   : {
  //       document: documentLight,
  //       clock: clockLight,
  //       mobilePhone: mobilePhoneLight,
  //     };
});

const { kycSession } = storeToRefs(useKycSessionStore());
const poaRequired = kycSession.value?.isPoaRequired();

async function saveConsent() {
  Mixpanel.instance.trackConsentGiven();
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }
  if (!kycSession.value.region) {
    throw new Error('Missing region code for KYC consent');
  }
  kycSession.value.cardholder.saveConsentForRegion({
    kycSessionId: kycSession.value.id,
    region: kycSession.value.region,
  });
  return true;
}

const generalTermsOfUse = ref<Document>();
const privacyPolicy = ref<Document>();
const disclosures = ref<Document[]>([]);

async function getKycConsentDocuments() {
  if (!kycSession.value) {
    throw new Error('Kyc session not found');
  }

  const { partnerAccountId, region } = kycSession.value;

  if (!region) {
    throw new Error('Missing region code for KYC consent');
  }

  if (!partnerAccountId) {
    throw new Error('Missing partner account id for KYC consent');
  }

  const regions = Regions.forAccount(partnerAccountId);
  await regions.load();
  const documents = regions.getOnboardingDocuments(region);
  generalTermsOfUse.value = documents.generalTermsOfUse;
  privacyPolicy.value = documents.privacyPolicy;
  disclosures.value = documents.disclosures ?? [];
}

onBeforeMount(async () => {
  await getKycConsentDocuments();
});
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col" data-test="screen-kyc-consent">
      <h1 class="mb-4 text-3xl">Here's what we need to activate your card</h1>
      <p class="text-light-secondary dark:text-dark-secondary mb-6">
        To activate your card, we need to verify your identity to follow regulation requirements.
      </p>
      <ul class="mb-10">
        <StartItem :icon="icons.clock" text="5 minutes" />
        <StartItem
          :icon="icons.document"
          text="An official form of identification (such
        as your passport)"
        />
        <StartItem
          v-if="poaRequired"
          :icon="icons.house"
          text="A proof of address document (such as a
        bank statement)"
        />
      </ul>
      <p class="text-light-secondary dark:text-dark-secondary">
        By proceeding, you agree to the
        <ExternalLink
          :title="generalTermsOfUse?.title ?? ''"
          :source="generalTermsOfUse?.source ?? ''"
        />, consent to the collection, use, and disclosure of your personal information in
        accordance with the
        <ExternalLink :title="privacyPolicy?.title ?? ''" :source="privacyPolicy?.source ?? ''" />
        <span v-if="disclosures.length > 0"
          >, and acknowledge that you have received, read, and understood the
          <span v-for="(doc, index) in disclosures" :key="index">
            <ExternalLink :title="doc.title" :source="doc.source" />
            <span v-if="index < disclosures.length - 2">, </span>
            <span v-if="index === disclosures.length - 2"> and </span>
          </span> </span
        >.
      </p>
    </div>
    <template #button-bar>
      <FlowNavigationButton aria-label="Back" text="Back" direction="back" />
      <FlowNavigationButton
        aria-label="Agree"
        text="I agree"
        direction="forward"
        :preNavigationAction="saveConsent"
      />
    </template>
  </KycSessionLayout>
</template>
